/**
 * Shape preset definitions. Each preset is pure data plus pure functions over a
 * pixel box — no React, no DOM. This mirrors how PowerPoint computes preset
 * geometry at the shape's real size rather than scaling a unit square, which is
 * why strokes stay uniform and corner radii stay circular under any aspect.
 */
import {
  boxProjector,
  clamp,
  ellipsePoint,
  fmt,
  polygon,
  regularPolygonPoints,
} from './primitives';

/** The ten vertices of a 5-point star on the unit circle, apex at the top. */
const star5Points = (inner) =>
  Array.from({ length: 10 }, (_, i) => {
    const angle = ((-90 + i * 36) * Math.PI) / 180;
    const r = i % 2 === 0 ? 1 : inner;
    return [r * Math.cos(angle), r * Math.sin(angle)];
  });

/** A centred rectangle inscribed in the box's ellipse — the default text area
 *  for round shapes, where the corners of the box are outside the figure. */
const centredHalfRect = (b) => ({
  x: b.x + b.width / 2 - b.width / (2 * Math.SQRT2),
  y: b.y + b.height / 2 - b.height / (2 * Math.SQRT2),
  width: b.width / Math.SQRT2,
  height: b.height / Math.SQRT2,
});

/** Pointer position to a fraction of a turn, measured from the box's centre. */
const turnsFromPoint = (b, px, py) => {
  const angle = Math.atan2(py - (b.y + b.height / 2), px - (b.x + b.width / 2));
  return ((angle / (2 * Math.PI)) + 1) % 1;
};

/** End angle for a sweep, kept ahead of the start so arcs never invert. */
const sweepEnd = (start, end) => (end > start ? end : end + 1);

/** A closed ellipse subpath, scaled about the box centre. `reverse` flips the
 *  winding so the subpath punches a hole under the nonzero fill rule. */
const ellipsePath = (b, scaleX = 1, scaleY = 1, reverse = false) => {
  const rx = (b.width / 2) * scaleX;
  const ry = (b.height / 2) * scaleY;
  const cx = b.x + b.width / 2;
  const cy = b.y + b.height / 2;
  const sweep = reverse ? 0 : 1;
  return [
    `M ${fmt(cx - rx)} ${fmt(cy)}`,
    `A ${fmt(rx)} ${fmt(ry)} 0 0 ${sweep} ${fmt(cx + rx)} ${fmt(cy)}`,
    `A ${fmt(rx)} ${fmt(ry)} 0 0 ${sweep} ${fmt(cx - rx)} ${fmt(cy)}`,
    'Z',
  ].join(' ');
};

/** A sector of the box's inscribed ellipse. `throughCentre` closes via the
 *  centre for a pie; otherwise the ends join directly, giving a chord. */
const sectorPath = (b, startTurns, endTurns, throughCentre) => {
  const a1 = clamp(startTurns, 0, 1);
  const a2 = sweepEnd(a1, clamp(endTurns, 0, 1));
  const [x1, y1] = ellipsePoint(b, a1);
  const [x2, y2] = ellipsePoint(b, a2);
  const large = a2 - a1 > 0.5 ? 1 : 0;
  const arc = `A ${fmt(b.width / 2)} ${fmt(b.height / 2)} 0 ${large} 1 ${fmt(x2)} ${fmt(y2)}`;
  const cx = b.x + b.width / 2;
  const cy = b.y + b.height / 2;
  return throughCentre
    ? `M ${fmt(cx)} ${fmt(cy)} L ${fmt(x1)} ${fmt(y1)} ${arc} Z`
    : `M ${fmt(x1)} ${fmt(y1)} ${arc} Z`;
};

/** Draggable handles on the two ends of a sector. */
const sectorHandles = (b, adj) => {
  const [x1, y1] = ellipsePoint(b, clamp(adj.adj1, 0, 1));
  const [x2, y2] = ellipsePoint(b, clamp(adj.adj2, 0, 1));
  return [
    { x: x1, y: y1, adjustX: 'adj1', adjustY: 'adj1' },
    { x: x2, y: y2, adjustX: 'adj2', adjustY: 'adj2' },
  ];
};

/** A regular n-gon preset, normalised to fill its box. */
const regularPolygon = (name, legacyTokens, n) => ({
  name,
  legacyTokens,
  adjustments: [],
  path: (b) => {
    const pts = regularPolygonPoints(n);
    return polygon(pts.map(boxProjector(pts, b)));
  },
  textRect: (b) => {
    const pts = regularPolygonPoints(n);
    const project = boxProjector(pts, b);
    // Inscribed in the incircle, whose radius is cos(pi/n) of the circumradius.
    const half = Math.cos(Math.PI / n) / Math.SQRT2;
    const [x0, y0] = project([-half, -half]);
    const [x1, y1] = project([half, half]);
    return { x: x0, y: y0, width: x1 - x0, height: y1 - y0 };
  },
  handles: () => [],
});

export const PRESETS = {
  rect: {
    name: 'rect',
    legacyTokens: ['rectangle', 'recta', 'squr2', 'square'],
    adjustments: [],
    path: (b) =>
      polygon([
        [b.x, b.y],
        [b.x + b.width, b.y],
        [b.x + b.width, b.y + b.height],
        [b.x, b.y + b.height],
      ]),
    textRect: (b) => ({ ...b }),
    handles: () => [],
  },

  roundRect: {
    name: 'roundRect',
    legacyTokens: ['round'],
    adjustments: [
      {
        name: 'adj',
        default: 0.16667,
        min: 0,
        max: 0.5,
        fromPoint: (b, px) => (px - b.x) / Math.min(b.width, b.height),
      },
    ],
    path: (b, adj) => {
      const r = clamp(adj.adj, 0, 0.5) * Math.min(b.width, b.height);
      const { x, y, width: w, height: h } = b;
      const a = `A ${fmt(r)} ${fmt(r)} 0 0 1`;
      return [
        `M ${fmt(x + r)} ${fmt(y)}`,
        `L ${fmt(x + w - r)} ${fmt(y)}`,
        `${a} ${fmt(x + w)} ${fmt(y + r)}`,
        `L ${fmt(x + w)} ${fmt(y + h - r)}`,
        `${a} ${fmt(x + w - r)} ${fmt(y + h)}`,
        `L ${fmt(x + r)} ${fmt(y + h)}`,
        `${a} ${fmt(x)} ${fmt(y + h - r)}`,
        `L ${fmt(x)} ${fmt(y + r)}`,
        `${a} ${fmt(x + r)} ${fmt(y)}`,
        'Z',
      ].join(' ');
    },
    textRect: (b, adj) => {
      // Inset by the part of the corner that actually eats into the edge.
      const r = clamp(adj.adj, 0, 0.5) * Math.min(b.width, b.height) * 0.293;
      return {
        x: b.x + r,
        y: b.y + r,
        width: Math.max(0, b.width - 2 * r),
        height: Math.max(0, b.height - 2 * r),
      };
    },
    handles: (b, adj) => [
      {
        x: b.x + clamp(adj.adj, 0, 0.5) * Math.min(b.width, b.height),
        y: b.y,
        adjustX: 'adj',
        adjustY: null,
      },
    ],
  },

  snipRect: {
    name: 'snipRect',
    legacyTokens: ['single', 'singl'],
    adjustments: [
      {
        name: 'adj',
        default: 0.16667,
        min: 0,
        max: 0.5,
        fromPoint: (b, px) =>
          (b.x + b.width - px) / Math.min(b.width, b.height),
      },
    ],
    path: (b, adj) => {
      const s = clamp(adj.adj, 0, 0.5) * Math.min(b.width, b.height);
      return polygon([
        [b.x, b.y],
        [b.x + b.width - s, b.y],
        [b.x + b.width, b.y + s],
        [b.x + b.width, b.y + b.height],
        [b.x, b.y + b.height],
      ]);
    },
    textRect: (b, adj) => {
      const s = clamp(adj.adj, 0, 0.5) * Math.min(b.width, b.height);
      return {
        x: b.x,
        y: b.y + s,
        width: Math.max(0, b.width - s),
        height: Math.max(0, b.height - s),
      };
    },
    handles: (b, adj) => [
      {
        x: b.x + b.width - clamp(adj.adj, 0, 0.5) * Math.min(b.width, b.height),
        y: b.y,
        adjustX: 'adj',
        adjustY: null,
      },
    ],
  },

  triangle: {
    name: 'triangle',
    legacyTokens: ['triangle', 'trian'],
    adjustments: [
      {
        name: 'adj',
        default: 0.5,
        min: 0,
        max: 1,
        fromPoint: (b, px) => (px - b.x) / b.width,
      },
    ],
    path: (b, adj) => {
      const apex = b.x + clamp(adj.adj, 0, 1) * b.width;
      return polygon([
        [apex, b.y],
        [b.x + b.width, b.y + b.height],
        [b.x, b.y + b.height],
      ]);
    },
    textRect: (b, adj) => {
      const apex = b.x + clamp(adj.adj, 0, 1) * b.width;
      // At mid-height the interior spans between the midpoints of both edges.
      return {
        x: (apex + b.x) / 2,
        y: b.y + b.height / 2,
        width: b.width / 2,
        height: b.height / 2,
      };
    },
    handles: (b, adj) => [
      { x: b.x + clamp(adj.adj, 0, 1) * b.width, y: b.y, adjustX: 'adj', adjustY: null },
    ],
  },

  rightTriangle: {
    name: 'rightTriangle',
    legacyTokens: ['rightTriangle', 'right'],
    adjustments: [],
    path: (b) =>
      polygon([
        [b.x, b.y],
        [b.x + b.width, b.y + b.height],
        [b.x, b.y + b.height],
      ]),
    textRect: (b) => ({
      x: b.x,
      y: b.y + b.height / 2,
      width: b.width / 2,
      height: b.height / 2,
    }),
    handles: () => [],
  },

  diamond: {
    name: 'diamond',
    legacyTokens: ['diamond', 'diamo'],
    adjustments: [],
    path: (b) =>
      polygon([
        [b.x + b.width / 2, b.y],
        [b.x + b.width, b.y + b.height / 2],
        [b.x + b.width / 2, b.y + b.height],
        [b.x, b.y + b.height / 2],
      ]),
    // Exact inscribed rectangle of a rhombus: half width, half height, centred.
    textRect: (b) => ({
      x: b.x + b.width / 4,
      y: b.y + b.height / 4,
      width: b.width / 2,
      height: b.height / 2,
    }),
    handles: () => [],
  },

  trapezoid: {
    name: 'trapezoid',
    legacyTokens: ['trapezoid', 'trape'],
    adjustments: [
      {
        name: 'adj',
        default: 0.25,
        min: 0,
        max: 0.5,
        fromPoint: (b, px) => (px - b.x) / b.width,
      },
    ],
    path: (b, adj) => {
      const i = clamp(adj.adj, 0, 0.5) * b.width;
      return polygon([
        [b.x + i, b.y],
        [b.x + b.width - i, b.y],
        [b.x + b.width, b.y + b.height],
        [b.x, b.y + b.height],
      ]);
    },
    // The top edge is the narrowest slice, so it bounds a full-height rect.
    textRect: (b, adj) => {
      const i = clamp(adj.adj, 0, 0.5) * b.width;
      return {
        x: b.x + i,
        y: b.y,
        width: Math.max(1, b.width - 2 * i),
        height: b.height,
      };
    },
    handles: (b, adj) => [
      { x: b.x + clamp(adj.adj, 0, 0.5) * b.width, y: b.y, adjustX: 'adj', adjustY: null },
    ],
  },

  parallelogram: {
    name: 'parallelogram',
    legacyTokens: ['parallelogram', 'paral'],
    adjustments: [
      {
        name: 'adj',
        default: 0.25,
        min: 0,
        max: 0.5,
        fromPoint: (b, px) => (px - b.x) / b.width,
      },
    ],
    path: (b, adj) => {
      const o = clamp(adj.adj, 0, 0.5) * b.width;
      return polygon([
        [b.x + o, b.y],
        [b.x + b.width, b.y],
        [b.x + b.width - o, b.y + b.height],
        [b.x, b.y + b.height],
      ]);
    },
    // Intersection of the top span [o, w] and the bottom span [0, w - o].
    textRect: (b, adj) => {
      const o = clamp(adj.adj, 0, 0.5) * b.width;
      return {
        x: b.x + o,
        y: b.y,
        width: Math.max(1, b.width - 2 * o),
        height: b.height,
      };
    },
    handles: (b, adj) => [
      { x: b.x + clamp(adj.adj, 0, 0.5) * b.width, y: b.y, adjustX: 'adj', adjustY: null },
    ],
  },

  plus: {
    name: 'plus',
    legacyTokens: ['plus', 'pluss'],
    adjustments: [
      {
        name: 'adj',
        default: 0.25,
        min: 0.01,
        max: 0.5,
        fromPoint: (b, px) => (px - b.x) / b.width,
      },
    ],
    path: (b, adj) => {
      const t = clamp(adj.adj, 0.01, 0.5);
      const ax = t * b.width;
      const ay = t * b.height;
      const { x, y, width: w, height: h } = b;
      return polygon([
        [x + ax, y],
        [x + w - ax, y],
        [x + w - ax, y + ay],
        [x + w, y + ay],
        [x + w, y + h - ay],
        [x + w - ax, y + h - ay],
        [x + w - ax, y + h],
        [x + ax, y + h],
        [x + ax, y + h - ay],
        [x, y + h - ay],
        [x, y + ay],
        [x + ax, y + ay],
      ]);
    },
    // The central square where the two arms overlap.
    textRect: (b, adj) => {
      const t = clamp(adj.adj, 0.01, 0.5);
      return {
        x: b.x + t * b.width,
        y: b.y + t * b.height,
        width: b.width * (1 - 2 * t),
        height: b.height * (1 - 2 * t),
      };
    },
    handles: (b, adj) => {
      const t = clamp(adj.adj, 0.01, 0.5);
      return [{ x: b.x + t * b.width, y: b.y + t * b.height, adjustX: 'adj', adjustY: null }];
    },
  },

  star5: {
    name: 'star5',
    legacyTokens: ['star', 'stars'],
    adjustments: [
      {
        name: 'adj',
        default: 0.38196,
        min: 0.05,
        max: 1,
        // Approximate inverse: the box-to-star mapping depends on the very
        // value being solved for, so this estimates from the current frame.
        // Good enough for a live drag, and the result is clamped either way.
        fromPoint: (b, px, py) => {
          const dx = (px - (b.x + b.width / 2)) / (b.width / 2);
          const dy = (py - (b.y + b.height / 2)) / (b.height / 2);
          return Math.sqrt(dx * dx + dy * dy);
        },
      },
    ],
    path: (b, adj) => {
      const pts = star5Points(clamp(adj.adj, 0.05, 1));
      return polygon(pts.map(boxProjector(pts, b)));
    },
    // Rect inscribed in the inner ellipse, which the star fully contains,
    // projected through the same normalisation the path uses.
    textRect: (b, adj) => {
      const inner = clamp(adj.adj, 0.05, 1);
      const pts = star5Points(inner);
      const project = boxProjector(pts, b);
      const half = inner / Math.SQRT2;
      const [x0, y0] = project([-half, -half]);
      const [x1, y1] = project([half, half]);
      return { x: x0, y: y0, width: x1 - x0, height: y1 - y0 };
    },
    handles: (b, adj) => {
      const inner = clamp(adj.adj, 0.05, 1);
      const pts = star5Points(inner);
      // The first inner vertex, at -90 + 36 degrees.
      const [x, y] = boxProjector(pts, b)(pts[1]);
      return [{ x, y, adjustX: 'adj', adjustY: 'adj' }];
    },
  },

  ellipse: {
    name: 'ellipse',
    legacyTokens: ['circle', 'circl', 'oval', 'ovals'],
    adjustments: [],
    path: (b) => {
      const rx = b.width / 2;
      const ry = b.height / 2;
      const cy = b.y + ry;
      return [
        `M ${fmt(b.x)} ${fmt(cy)}`,
        `A ${fmt(rx)} ${fmt(ry)} 0 0 1 ${fmt(b.x + b.width)} ${fmt(cy)}`,
        `A ${fmt(rx)} ${fmt(ry)} 0 0 1 ${fmt(b.x)} ${fmt(cy)}`,
        'Z',
      ].join(' ');
    },
    // Largest axis-aligned rectangle inscribed in an ellipse.
    textRect: (b) => ({
      x: b.x + b.width / 2 - b.width / (2 * Math.SQRT2),
      y: b.y + b.height / 2 - b.height / (2 * Math.SQRT2),
      width: b.width / Math.SQRT2,
      height: b.height / Math.SQRT2,
    }),
    handles: () => [],
  },

  cylinder: {
    name: 'cylinder',
    legacyTokens: ['cylinder', 'cylin'],
    adjustments: [
      {
        name: 'adj',
        default: 0.25,
        min: 0.01,
        max: 0.5,
        fromPoint: (b, px, py) => (2 * (py - b.y)) / b.height,
      },
    ],
    path: (b, adj) => {
      const ry = (clamp(adj.adj, 0.01, 0.5) * b.height) / 2;
      const rx = b.width / 2;
      const { x, y, width: w, height: h } = b;
      const top = y + ry;
      const bottom = y + h - ry;
      const arc = `A ${fmt(rx)} ${fmt(ry)} 0 0 1`;

      const silhouette = [
        `M ${fmt(x)} ${fmt(top)}`,
        `${arc} ${fmt(x + w)} ${fmt(top)}`,
        `L ${fmt(x + w)} ${fmt(bottom)}`,
        `${arc} ${fmt(x)} ${fmt(bottom)}`,
        'Z',
      ].join(' ');

      const rim = [
        `M ${fmt(x)} ${fmt(top)}`,
        `${arc} ${fmt(x + w)} ${fmt(top)}`,
        `${arc} ${fmt(x)} ${fmt(top)}`,
        'Z',
      ].join(' ');

      return `${silhouette} ${rim}`;
    },
    textRect: (b, adj) => {
      const ry = (clamp(adj.adj, 0.01, 0.5) * b.height) / 2;
      return {
        x: b.x,
        y: b.y + 2 * ry,
        width: b.width,
        height: Math.max(1, b.height - 3 * ry),
      };
    },
    handles: (b, adj) => {
      const ry = (clamp(adj.adj, 0.01, 0.5) * b.height) / 2;
      return [{ x: b.x + b.width / 2, y: b.y + 2 * ry, adjustX: null, adjustY: 'adj' }];
    },
  },

  heart: {
    name: 'heart',
    legacyTokens: ['heart'],
    adjustments: [],
    path: (b) => {
      const { x, y, width: w, height: h } = b;
      const px = (f) => fmt(x + f * w);
      const py = (f) => fmt(y + f * h);
      // Control points all lie inside the box, so the bezier hull does too.
      return [
        `M ${px(0.5)} ${py(1)}`,
        `C ${px(0.09)} ${py(0.72)} ${px(0)} ${py(0.53)} ${px(0)} ${py(0.33)}`,
        `C ${px(0)} ${py(0.13)} ${px(0.16)} ${py(0)} ${px(0.31)} ${py(0)}`,
        `C ${px(0.4)} ${py(0)} ${px(0.47)} ${py(0.05)} ${px(0.5)} ${py(0.11)}`,
        `C ${px(0.53)} ${py(0.05)} ${px(0.6)} ${py(0)} ${px(0.69)} ${py(0)}`,
        `C ${px(0.84)} ${py(0)} ${px(1)} ${py(0.13)} ${px(1)} ${py(0.33)}`,
        `C ${px(1)} ${py(0.53)} ${px(0.91)} ${py(0.72)} ${px(0.5)} ${py(1)}`,
        'Z',
      ].join(' ');
    },
    textRect: (b) => ({
      x: b.x + 0.2 * b.width,
      y: b.y + 0.2 * b.height,
      width: 0.6 * b.width,
      height: 0.45 * b.height,
    }),
    handles: () => [],
  },

  wedgeRectCallout: {
    name: 'wedgeRectCallout',
    legacyTokens: ['callout', 'callo'],
    adjustments: [
      {
        name: 'adj1',
        default: 0.2,
        min: 0,
        max: 1,
        fromPoint: (b, px) => (px - b.x) / b.width,
      },
      {
        name: 'adj2',
        default: 1,
        min: 0,
        max: 1,
        fromPoint: (b, px, py) => (py - b.y - b.height * 0.75) / (b.height * 0.25),
      },
    ],
    path: (b, adj) => {
      const { x, y, width: w, height: h } = b;
      const bodyH = h * 0.75;
      const tipX = x + clamp(adj.adj1, 0, 1) * w;
      const tipY = y + bodyH + clamp(adj.adj2, 0, 1) * (h - bodyH);
      return polygon([
        [x, y],
        [x + w, y],
        [x + w, y + bodyH],
        [x + 0.4 * w, y + bodyH],
        [tipX, tipY],
        [x + 0.25 * w, y + bodyH],
        [x, y + bodyH],
      ]);
    },
    textRect: (b) => ({
      x: b.x,
      y: b.y,
      width: b.width,
      height: b.height * 0.75,
    }),
    handles: (b, adj) => {
      const bodyH = b.height * 0.75;
      return [
        {
          x: b.x + clamp(adj.adj1, 0, 1) * b.width,
          y: b.y + bodyH + clamp(adj.adj2, 0, 1) * (b.height - bodyH),
          adjustX: 'adj1',
          adjustY: 'adj2',
        },
      ];
    },
  },

  // --- Regular polygons ------------------------------------------------------
  pentagon: regularPolygon('pentagon', ['pentagon'], 5),
  heptagon: regularPolygon('heptagon', ['heptagon'], 7),
  decagon: regularPolygon('decagon', ['decagon'], 10),
  dodecagon: regularPolygon('dodecagon', ['dodecagon'], 12),

  hexagon: {
    name: 'hexagon',
    legacyTokens: ['hexagon'],
    adjustments: [
      {
        name: 'adj',
        default: 0.25,
        min: 0,
        max: 0.5,
        fromPoint: (b, px) => (px - b.x) / b.width,
      },
    ],
    path: (b, adj) => {
      const i = clamp(adj.adj, 0, 0.5) * b.width;
      const { x, y, width: w, height: h } = b;
      return polygon([
        [x + i, y],
        [x + w - i, y],
        [x + w, y + h / 2],
        [x + w - i, y + h],
        [x + i, y + h],
        [x, y + h / 2],
      ]);
    },
    textRect: (b, adj) => {
      const i = clamp(adj.adj, 0, 0.5) * b.width;
      return {
        x: b.x + i,
        y: b.y,
        width: Math.max(1, b.width - 2 * i),
        height: b.height,
      };
    },
    handles: (b, adj) => [
      { x: b.x + clamp(adj.adj, 0, 0.5) * b.width, y: b.y, adjustX: 'adj', adjustY: null },
    ],
  },

  octagon: {
    name: 'octagon',
    legacyTokens: ['octagon'],
    adjustments: [
      {
        name: 'adj',
        default: 0.29289,
        min: 0,
        max: 0.5,
        fromPoint: (b, px) => (px - b.x) / Math.min(b.width, b.height),
      },
    ],
    path: (b, adj) => {
      const s = clamp(adj.adj, 0, 0.5) * Math.min(b.width, b.height);
      const { x, y, width: w, height: h } = b;
      return polygon([
        [x + s, y],
        [x + w - s, y],
        [x + w, y + s],
        [x + w, y + h - s],
        [x + w - s, y + h],
        [x + s, y + h],
        [x, y + h - s],
        [x, y + s],
      ]);
    },
    textRect: (b, adj) => {
      const s = clamp(adj.adj, 0, 0.5) * Math.min(b.width, b.height);
      return {
        x: b.x + s * 0.3,
        y: b.y + s * 0.3,
        width: Math.max(1, b.width - s * 0.6),
        height: Math.max(1, b.height - s * 0.6),
      };
    },
    handles: (b, adj) => [
      {
        x: b.x + clamp(adj.adj, 0, 0.5) * Math.min(b.width, b.height),
        y: b.y,
        adjustX: 'adj',
        adjustY: null,
      },
    ],
  },

  // --- Frames and corners ----------------------------------------------------
  plaque: {
    name: 'plaque',
    legacyTokens: ['plaque'],
    adjustments: [
      {
        name: 'adj',
        default: 0.16667,
        min: 0,
        max: 0.5,
        fromPoint: (b, px) => (px - b.x) / Math.min(b.width, b.height),
      },
    ],
    // A rounded rectangle with the corner arcs turned inside out (sweep 0).
    path: (b, adj) => {
      const s = clamp(adj.adj, 0, 0.5) * Math.min(b.width, b.height);
      const { x, y, width: w, height: h } = b;
      const a = `A ${fmt(s)} ${fmt(s)} 0 0 0`;
      return [
        `M ${fmt(x + s)} ${fmt(y)}`,
        `L ${fmt(x + w - s)} ${fmt(y)}`,
        `${a} ${fmt(x + w)} ${fmt(y + s)}`,
        `L ${fmt(x + w)} ${fmt(y + h - s)}`,
        `${a} ${fmt(x + w - s)} ${fmt(y + h)}`,
        `L ${fmt(x + s)} ${fmt(y + h)}`,
        `${a} ${fmt(x)} ${fmt(y + h - s)}`,
        `L ${fmt(x)} ${fmt(y + s)}`,
        `${a} ${fmt(x + s)} ${fmt(y)}`,
        'Z',
      ].join(' ');
    },
    textRect: (b, adj) => {
      const s = clamp(adj.adj, 0, 0.5) * Math.min(b.width, b.height);
      return {
        x: b.x + s,
        y: b.y + s,
        width: Math.max(1, b.width - 2 * s),
        height: Math.max(1, b.height - 2 * s),
      };
    },
    handles: (b, adj) => [
      {
        x: b.x + clamp(adj.adj, 0, 0.5) * Math.min(b.width, b.height),
        y: b.y,
        adjustX: 'adj',
        adjustY: null,
      },
    ],
  },

  frame: {
    name: 'frame',
    legacyTokens: ['frame'],
    adjustments: [
      {
        name: 'adj',
        default: 0.125,
        min: 0.01,
        max: 0.5,
        fromPoint: (b, px) => (px - b.x) / Math.min(b.width, b.height),
      },
    ],
    // Outer ring clockwise, inner ring anticlockwise: under the nonzero fill
    // rule the opposed winding punches the hole.
    path: (b, adj) => {
      const t = clamp(adj.adj, 0.01, 0.5) * Math.min(b.width, b.height);
      const { x, y, width: w, height: h } = b;
      const outer = polygon([
        [x, y],
        [x + w, y],
        [x + w, y + h],
        [x, y + h],
      ]);
      const inner = polygon([
        [x + t, y + t],
        [x + t, y + h - t],
        [x + w - t, y + h - t],
        [x + w - t, y + t],
      ]);
      return `${outer} ${inner}`;
    },
    textRect: (b) => ({ ...b }),
    handles: (b, adj) => [
      {
        x: b.x + clamp(adj.adj, 0.01, 0.5) * Math.min(b.width, b.height),
        y: b.y,
        adjustX: 'adj',
        adjustY: null,
      },
    ],
  },

  halfFrame: {
    name: 'halfFrame',
    legacyTokens: ['halfFrame'],
    adjustments: [
      {
        name: 'adj1',
        default: 0.2,
        min: 0.01,
        max: 0.9,
        fromPoint: (b, px, py) => (py - b.y) / b.height,
      },
      {
        name: 'adj2',
        default: 0.2,
        min: 0.01,
        max: 0.9,
        fromPoint: (b, px) => (px - b.x) / b.width,
      },
    ],
    // Top and left arms, mitred at 45 degrees where each arm ends.
    path: (b, adj) => {
      const th = clamp(adj.adj1, 0.01, 0.9) * b.height;
      const tw = clamp(adj.adj2, 0.01, 0.9) * b.width;
      const { x, y, width: w, height: h } = b;
      return polygon([
        [x, y],
        [x + w, y],
        [x + w - th, y + th],
        [x + tw, y + th],
        [x + tw, y + h - tw],
        [x, y + h],
      ]);
    },
    textRect: (b, adj) => {
      const th = clamp(adj.adj1, 0.01, 0.9) * b.height;
      return { x: b.x, y: b.y, width: b.width, height: Math.max(1, th) };
    },
    handles: (b, adj) => [
      {
        x: b.x + clamp(adj.adj2, 0.01, 0.9) * b.width,
        y: b.y + clamp(adj.adj1, 0.01, 0.9) * b.height,
        adjustX: 'adj2',
        adjustY: 'adj1',
      },
    ],
  },

  corner: {
    name: 'corner',
    legacyTokens: ['corner'],
    adjustments: [
      {
        name: 'adj1',
        default: 0.5,
        min: 0.01,
        max: 0.99,
        fromPoint: (b, px, py) => (b.y + b.height - py) / b.height,
      },
      {
        name: 'adj2',
        default: 0.5,
        min: 0.01,
        max: 0.99,
        fromPoint: (b, px) => (px - b.x) / b.width,
      },
    ],
    // An L: a vertical arm down the left, a horizontal arm along the bottom.
    path: (b, adj) => {
      const th = clamp(adj.adj1, 0.01, 0.99) * b.height;
      const tw = clamp(adj.adj2, 0.01, 0.99) * b.width;
      const { x, y, width: w, height: h } = b;
      return polygon([
        [x, y],
        [x + tw, y],
        [x + tw, y + h - th],
        [x + w, y + h - th],
        [x + w, y + h],
        [x, y + h],
      ]);
    },
    textRect: (b, adj) => {
      const th = clamp(adj.adj1, 0.01, 0.99) * b.height;
      return {
        x: b.x,
        y: b.y + b.height - th,
        width: b.width,
        height: Math.max(1, th),
      };
    },
    handles: (b, adj) => [
      {
        x: b.x + clamp(adj.adj2, 0.01, 0.99) * b.width,
        y: b.y + b.height - clamp(adj.adj1, 0.01, 0.99) * b.height,
        adjustX: 'adj2',
        adjustY: 'adj1',
      },
    ],
  },

  diagStripe: {
    name: 'diagStripe',
    legacyTokens: ['diagStripe'],
    adjustments: [
      {
        name: 'adj',
        default: 0.5,
        min: 0.01,
        max: 0.99,
        fromPoint: (b, px, py) => 1 - (py - b.y) / b.height,
      },
    ],
    // The band between the anti-diagonal and a parallel line, clipped to the box.
    path: (b, adj) => {
      const a = clamp(adj.adj, 0.01, 0.99);
      const { x, y, width: w, height: h } = b;
      return polygon([
        [x, y + h * (1 - a)],
        [x + w * (1 - a), y],
        [x + w, y],
        [x, y + h],
      ]);
    },
    textRect: (b) => ({
      x: b.x + b.width * 0.25,
      y: b.y + b.height * 0.25,
      width: b.width * 0.5,
      height: b.height * 0.5,
    }),
    handles: (b, adj) => [
      {
        x: b.x,
        y: b.y + b.height * (1 - clamp(adj.adj, 0.01, 0.99)),
        adjustX: null,
        adjustY: 'adj',
      },
    ],
  },

  foldedCorner: {
    name: 'foldedCorner',
    legacyTokens: ['foldedCorner'],
    adjustments: [
      {
        name: 'adj',
        default: 0.16667,
        min: 0.01,
        max: 0.5,
        fromPoint: (b, px) => (b.x + b.width - px) / Math.min(b.width, b.height),
      },
    ],
    path: (b, adj) => {
      const s = clamp(adj.adj, 0.01, 0.5) * Math.min(b.width, b.height);
      const { x, y, width: w, height: h } = b;
      const body = polygon([
        [x, y],
        [x + w, y],
        [x + w, y + h - s],
        [x + w - s, y + h],
        [x, y + h],
      ]);
      // The turned-back flap, drawn over the clipped corner.
      const fold = polygon([
        [x + w - s, y + h],
        [x + w, y + h - s],
        [x + w - s, y + h - s],
      ]);
      return `${body} ${fold}`;
    },
    textRect: (b, adj) => {
      const s = clamp(adj.adj, 0.01, 0.5) * Math.min(b.width, b.height);
      return { x: b.x, y: b.y, width: b.width, height: Math.max(1, b.height - s) };
    },
    handles: (b, adj) => [
      {
        x: b.x + b.width - clamp(adj.adj, 0.01, 0.5) * Math.min(b.width, b.height),
        y: b.y + b.height,
        adjustX: 'adj',
        adjustY: null,
      },
    ],
  },

  cube: {
    name: 'cube',
    legacyTokens: ['cube'],
    adjustments: [
      {
        name: 'adj',
        default: 0.25,
        min: 0.01,
        max: 0.5,
        fromPoint: (b, px, py) => (py - b.y) / Math.min(b.width, b.height),
      },
    ],
    // Front face, top face and right face as separate subpaths: all fill with
    // the same colour, and each keeps its own stroked outline.
    path: (b, adj) => {
      const d = clamp(adj.adj, 0.01, 0.5) * Math.min(b.width, b.height);
      const { x, y, width: w, height: h } = b;
      const front = polygon([
        [x, y + d],
        [x + w - d, y + d],
        [x + w - d, y + h],
        [x, y + h],
      ]);
      const top = polygon([
        [x, y + d],
        [x + d, y],
        [x + w, y],
        [x + w - d, y + d],
      ]);
      const side = polygon([
        [x + w - d, y + d],
        [x + w, y],
        [x + w, y + h - d],
        [x + w - d, y + h],
      ]);
      return `${front} ${top} ${side}`;
    },
    textRect: (b, adj) => {
      const d = clamp(adj.adj, 0.01, 0.5) * Math.min(b.width, b.height);
      return {
        x: b.x,
        y: b.y + d,
        width: Math.max(1, b.width - d),
        height: Math.max(1, b.height - d),
      };
    },
    handles: (b, adj) => [
      {
        x: b.x + clamp(adj.adj, 0.01, 0.5) * Math.min(b.width, b.height),
        y: b.y,
        adjustX: null,
        adjustY: 'adj',
      },
    ],
  },

  bevel: {
    name: 'bevel',
    legacyTokens: ['bevel'],
    adjustments: [
      {
        name: 'adj',
        default: 0.125,
        min: 0.01,
        max: 0.5,
        fromPoint: (b, px) => (px - b.x) / Math.min(b.width, b.height),
      },
    ],
    // Outer and inner rectangles plus the four corner mitres, so the bevel
    // reads as raised regardless of fill colour.
    path: (b, adj) => {
      const t = clamp(adj.adj, 0.01, 0.5) * Math.min(b.width, b.height);
      const { x, y, width: w, height: h } = b;
      const outer = polygon([
        [x, y],
        [x + w, y],
        [x + w, y + h],
        [x, y + h],
      ]);
      const inner = polygon([
        [x + t, y + t],
        [x + w - t, y + t],
        [x + w - t, y + h - t],
        [x + t, y + h - t],
      ]);
      const mitres = [
        `M ${fmt(x)} ${fmt(y)} L ${fmt(x + t)} ${fmt(y + t)}`,
        `M ${fmt(x + w)} ${fmt(y)} L ${fmt(x + w - t)} ${fmt(y + t)}`,
        `M ${fmt(x + w)} ${fmt(y + h)} L ${fmt(x + w - t)} ${fmt(y + h - t)}`,
        `M ${fmt(x)} ${fmt(y + h)} L ${fmt(x + t)} ${fmt(y + h - t)}`,
      ].join(' ');
      return `${outer} ${inner} ${mitres}`;
    },
    textRect: (b, adj) => {
      const t = clamp(adj.adj, 0.01, 0.5) * Math.min(b.width, b.height);
      return {
        x: b.x + t,
        y: b.y + t,
        width: Math.max(1, b.width - 2 * t),
        height: Math.max(1, b.height - 2 * t),
      };
    },
    handles: (b, adj) => [
      {
        x: b.x + clamp(adj.adj, 0.01, 0.5) * Math.min(b.width, b.height),
        y: b.y,
        adjustX: 'adj',
        adjustY: null,
      },
    ],
  },

  lightningBolt: {
    name: 'lightningBolt',
    legacyTokens: ['lightningBolt', 'bolt'],
    adjustments: [],
    path: (b) => {
      const pts = [
        [0.273, 0], [0.578, 0.383], [0.461, 0.426], [0.789, 0.68],
        [0.68, 0.727], [1, 1], [0.539, 0.766], [0.629, 0.707],
        [0.32, 0.516], [0.43, 0.469], [0.078, 0.211],
      ];
      return polygon(pts.map(boxProjector(pts, b)));
    },
    textRect: (b) => ({
      x: b.x + b.width * 0.3,
      y: b.y + b.height * 0.35,
      width: b.width * 0.4,
      height: b.height * 0.3,
    }),
    handles: () => [],
  },

  // --- Sectors and rings -----------------------------------------------------
  // Angles are stored as fractions of a turn, keeping every adjust value on the
  // same 0..1 scale as the rest of the registry.
  pie: {
    name: 'pie',
    legacyTokens: ['pie'],
    adjustments: [
      { name: 'adj1', default: 0, min: 0, max: 1, fromPoint: turnsFromPoint },
      { name: 'adj2', default: 0.75, min: 0, max: 1, fromPoint: turnsFromPoint },
    ],
    path: (b, adj) => sectorPath(b, adj.adj1, adj.adj2, true),
    textRect: centredHalfRect,
    handles: (b, adj) => sectorHandles(b, adj),
  },

  chord: {
    name: 'chord',
    legacyTokens: ['chord'],
    adjustments: [
      { name: 'adj1', default: 0.125, min: 0, max: 1, fromPoint: turnsFromPoint },
      { name: 'adj2', default: 0.75, min: 0, max: 1, fromPoint: turnsFromPoint },
    ],
    path: (b, adj) => sectorPath(b, adj.adj1, adj.adj2, false),
    textRect: centredHalfRect,
    handles: (b, adj) => sectorHandles(b, adj),
  },

  arc: {
    name: 'arc',
    legacyTokens: ['arc'],
    adjustments: [
      { name: 'adj1', default: 0.75, min: 0, max: 1, fromPoint: turnsFromPoint },
      { name: 'adj2', default: 0, min: 0, max: 1, fromPoint: turnsFromPoint },
    ],
    path: (b, adj) => sectorPath(b, adj.adj1, adj.adj2, false),
    textRect: centredHalfRect,
    handles: (b, adj) => sectorHandles(b, adj),
  },

  donut: {
    name: 'donut',
    legacyTokens: ['donut'],
    adjustments: [
      {
        name: 'adj',
        default: 0.25,
        min: 0.01,
        max: 0.5,
        fromPoint: (b, px) => (px - b.x) / b.width,
      },
    ],
    // Outer ring clockwise, inner ring anticlockwise punches the hole.
    path: (b, adj) => {
      const t = clamp(adj.adj, 0.01, 0.5);
      return `${ellipsePath(b, 1, 1)} ${ellipsePath(b, 1 - 2 * t, 1 - 2 * t, true)}`;
    },
    textRect: (b, adj) => {
      const inner = 1 - 2 * clamp(adj.adj, 0.01, 0.5);
      const halfW = (b.width / 2) * (inner / Math.SQRT2);
      const halfH = (b.height / 2) * (inner / Math.SQRT2);
      return {
        x: b.x + b.width / 2 - halfW,
        y: b.y + b.height / 2 - halfH,
        width: halfW * 2,
        height: halfH * 2,
      };
    },
    handles: (b, adj) => [
      {
        x: b.x + clamp(adj.adj, 0.01, 0.5) * b.width,
        y: b.y + b.height / 2,
        adjustX: 'adj',
        adjustY: null,
      },
    ],
  },

  noSmoking: {
    name: 'noSmoking',
    legacyTokens: ['noSmoking'],
    adjustments: [
      {
        name: 'adj',
        default: 0.18,
        min: 0.01,
        max: 0.5,
        fromPoint: (b, px) => (px - b.x) / b.width,
      },
    ],
    path: (b, adj) => {
      const t = clamp(adj.adj, 0.01, 0.5);
      const inner = 1 - 2 * t;
      const { width: w, height: h } = b;
      const cx = b.x + w / 2;
      const cy = b.y + h / 2;
      // The slash, sized to the inner circle and rotated 45 degrees.
      const r = (Math.min(w, h) / 2) * inner;
      const k = (t * Math.min(w, h)) / 2;
      const c = Math.SQRT1_2;
      const bar = polygon([
        [cx - r * c - k * c, cy + r * c - k * c],
        [cx + r * c - k * c, cy - r * c - k * c],
        [cx + r * c + k * c, cy - r * c + k * c],
        [cx - r * c + k * c, cy + r * c + k * c],
      ]);
      return `${ellipsePath(b, 1, 1)} ${ellipsePath(b, inner, inner, true)} ${bar}`;
    },
    textRect: centredHalfRect,
    handles: (b, adj) => [
      {
        x: b.x + clamp(adj.adj, 0.01, 0.5) * b.width,
        y: b.y + b.height / 2,
        adjustX: 'adj',
        adjustY: null,
      },
    ],
  },

  blockArc: {
    name: 'blockArc',
    legacyTokens: ['blockArc'],
    adjustments: [
      { name: 'adj1', default: 0.5, min: 0, max: 1, fromPoint: turnsFromPoint },
      { name: 'adj2', default: 0, min: 0, max: 1, fromPoint: turnsFromPoint },
      {
        name: 'adj3',
        default: 0.25,
        min: 0.01,
        max: 0.5,
        fromPoint: (b, px) => (px - b.x) / b.width,
      },
    ],
    path: (b, adj) => {
      const a1 = clamp(adj.adj1, 0, 1);
      const a2 = sweepEnd(a1, clamp(adj.adj2, 0, 1));
      const inner = 1 - 2 * clamp(adj.adj3, 0.01, 0.5);
      const rx = b.width / 2;
      const ry = b.height / 2;
      const [ox1, oy1] = ellipsePoint(b, a1);
      const [ox2, oy2] = ellipsePoint(b, a2);
      const scaled = { ...b, width: b.width * inner, height: b.height * inner,
        x: b.x + (b.width * (1 - inner)) / 2, y: b.y + (b.height * (1 - inner)) / 2 };
      const [ix1, iy1] = ellipsePoint(scaled, a1);
      const [ix2, iy2] = ellipsePoint(scaled, a2);
      const large = a2 - a1 > 0.5 ? 1 : 0;
      return [
        `M ${fmt(ox1)} ${fmt(oy1)}`,
        `A ${fmt(rx)} ${fmt(ry)} 0 ${large} 1 ${fmt(ox2)} ${fmt(oy2)}`,
        `L ${fmt(ix2)} ${fmt(iy2)}`,
        `A ${fmt(rx * inner)} ${fmt(ry * inner)} 0 ${large} 0 ${fmt(ix1)} ${fmt(iy1)}`,
        'Z',
      ].join(' ');
    },
    textRect: centredHalfRect,
    handles: (b, adj) => [
      {
        x: b.x + clamp(adj.adj3, 0.01, 0.5) * b.width,
        y: b.y + b.height / 2,
        adjustX: 'adj3',
        adjustY: null,
      },
    ],
  },

  teardrop: {
    name: 'teardrop',
    legacyTokens: ['teardrop'],
    adjustments: [
      {
        name: 'adj',
        default: 1,
        min: 0,
        max: 1,
        fromPoint: (b, px) => (px - (b.x + b.width / 2)) / (b.width / 2),
      },
    ],
    // Three quarter-arcs of the inscribed ellipse, with the top-right quadrant
    // pulled out to a corner.
    path: (b, adj) => {
      const t = clamp(adj.adj, 0, 1);
      const rx = b.width / 2;
      const ry = b.height / 2;
      const cx = b.x + rx;
      const cy = b.y + ry;
      const q = `A ${fmt(rx)} ${fmt(ry)} 0 0 1`;
      return [
        `M ${fmt(b.x)} ${fmt(cy)}`,
        `${q} ${fmt(cx)} ${fmt(b.y)}`,
        `L ${fmt(cx + rx * t)} ${fmt(cy - ry * t)}`,
        `L ${fmt(b.x + b.width)} ${fmt(cy)}`,
        `${q} ${fmt(cx)} ${fmt(b.y + b.height)}`,
        `${q} ${fmt(b.x)} ${fmt(cy)}`,
        'Z',
      ].join(' ');
    },
    textRect: (b) => ({
      x: b.x + b.width / 2 - b.width / (2 * Math.SQRT2),
      y: b.y + b.height / 2 - b.height / (2 * Math.SQRT2),
      width: b.width / Math.SQRT2,
      height: b.height / Math.SQRT2,
    }),
    handles: (b, adj) => [
      {
        x: b.x + b.width / 2 + (b.width / 2) * clamp(adj.adj, 0, 1),
        y: b.y + b.height / 2 - (b.height / 2) * clamp(adj.adj, 0, 1),
        adjustX: 'adj',
        adjustY: null,
      },
    ],
  },

  moon: {
    name: 'moon',
    legacyTokens: ['moon'],
    adjustments: [
      {
        name: 'adj',
        default: 0.5,
        min: 0.05,
        max: 0.95,
        fromPoint: (b, px) => 1 - (px - b.x) / b.width,
      },
    ],
    path: (b, adj) => {
      const t = clamp(adj.adj, 0.05, 0.95);
      const { x, y, width: w, height: h } = b;
      const ry = h / 2;
      return [
        `M ${fmt(x + w)} ${fmt(y)}`,
        `A ${fmt(w)} ${fmt(ry)} 0 0 0 ${fmt(x + w)} ${fmt(y + h)}`,
        `A ${fmt(w * (1 - t))} ${fmt(ry)} 0 0 1 ${fmt(x + w)} ${fmt(y)}`,
        'Z',
      ].join(' ');
    },
    textRect: (b) => ({
      x: b.x + b.width * 0.1,
      y: b.y + b.height * 0.35,
      width: b.width * 0.45,
      height: b.height * 0.3,
    }),
    handles: (b, adj) => [
      {
        x: b.x + b.width * (1 - clamp(adj.adj, 0.05, 0.95)),
        y: b.y + b.height / 2,
        adjustX: 'adj',
        adjustY: null,
      },
    ],
  },

  sun: {
    name: 'sun',
    legacyTokens: ['sun'],
    adjustments: [
      {
        name: 'adj',
        default: 0.25,
        min: 0.05,
        max: 0.45,
        fromPoint: (b, px) => 0.5 - (px - b.x) / b.width,
      },
    ],
    // A central disc with eight triangular rays reaching the box edge.
    path: (b, adj) => {
      const t = clamp(adj.adj, 0.05, 0.45);
      const core = 1 - 2 * t;
      const inner = { ...b,
        width: b.width * core, height: b.height * core,
        x: b.x + (b.width * (1 - core)) / 2, y: b.y + (b.height * (1 - core)) / 2 };
      const rays = Array.from({ length: 8 }, (_, i) => {
        const mid = i / 8;
        const spread = 1 / 32;
        return polygon([
          ellipsePoint(inner, mid - spread),
          ellipsePoint(b, mid),
          ellipsePoint(inner, mid + spread),
        ]);
      }).join(' ');
      return `${ellipsePath(inner, 1, 1)} ${rays}`;
    },
    textRect: (b, adj) => {
      const core = (1 - 2 * clamp(adj.adj, 0.05, 0.45)) / Math.SQRT2;
      return {
        x: b.x + b.width / 2 - (b.width / 2) * core,
        y: b.y + b.height / 2 - (b.height / 2) * core,
        width: b.width * core,
        height: b.height * core,
      };
    },
    handles: (b, adj) => [
      {
        x: b.x + b.width * (0.5 - clamp(adj.adj, 0.05, 0.45)),
        y: b.y + b.height / 2,
        adjustX: 'adj',
        adjustY: null,
      },
    ],
  },

  smileyFace: {
    name: 'smileyFace',
    legacyTokens: ['smileyFace', 'smiley'],
    adjustments: [
      {
        name: 'adj',
        default: 0.5,
        min: 0,
        max: 1,
        fromPoint: (b, px, py) => (py - b.y - b.height * 0.6) / (b.height * 0.35),
      },
    ],
    // Face, then eyes and mouth wound the other way so they read as holes
    // against whatever fill the shape carries.
    path: (b, adj) => {
      const t = clamp(adj.adj, 0, 1);
      const { x, y, width: w, height: h } = b;
      const eyeW = w * 0.09;
      const eyeH = h * 0.11;
      const eye = (cx) =>
        ellipsePath(
          { x: cx - eyeW / 2, y: y + h * 0.3 - eyeH / 2, width: eyeW, height: eyeH },
          1, 1, true
        );
      // Mouth: a lens between two arcs whose bow follows the adjust value.
      const mx1 = x + w * 0.29;
      const mx2 = x + w * 0.71;
      const my = y + h * 0.6;
      const bow = h * (0.06 + 0.22 * t);
      const mouth = [
        `M ${fmt(mx1)} ${fmt(my)}`,
        `Q ${fmt(x + w / 2)} ${fmt(my + bow)} ${fmt(mx2)} ${fmt(my)}`,
        `Q ${fmt(x + w / 2)} ${fmt(my + bow * 0.6)} ${fmt(mx1)} ${fmt(my)}`,
        'Z',
      ].join(' ');
      return [
        ellipsePath(b, 1, 1),
        eye(x + w * 0.32),
        eye(x + w * 0.68),
        mouth,
      ].join(' ');
    },
    textRect: centredHalfRect,
    handles: (b, adj) => [
      {
        x: b.x + b.width / 2,
        y: b.y + b.height * 0.6 + b.height * 0.35 * clamp(adj.adj, 0, 1),
        adjustX: null,
        adjustY: 'adj',
      },
    ],
  },

  cloud: {
    name: 'cloud',
    legacyTokens: ['cloud'],
    adjustments: [],
    // A ring of overlapping bumps around an ellipse; each arc bulges outward
    // from the previous vertex, giving the scalloped outline.
    path: (b) => {
      const bumps = 9;
      const rx = (b.width / bumps) * 0.85;
      const ry = (b.height / bumps) * 0.85;
      // Seat the vertices one bump-radius inside the box, so the scallops reach
      // the edge rather than bulging past it.
      const base = {
        x: b.x + rx,
        y: b.y + ry,
        width: b.width - 2 * rx,
        height: b.height - 2 * ry,
      };
      const pts = Array.from({ length: bumps }, (_, i) => ellipsePoint(base, i / bumps));
      const segments = pts.map((_, i) => {
        const [nx, ny] = pts[(i + 1) % bumps];
        return `A ${fmt(rx)} ${fmt(ry)} 0 0 1 ${fmt(nx)} ${fmt(ny)}`;
      });
      return [`M ${fmt(pts[0][0])} ${fmt(pts[0][1])}`, ...segments, 'Z'].join(' ');
    },
    textRect: centredHalfRect,
    handles: () => [],
  },
};
